/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { CodeSnippet, ListItem, UnorderedList } from 'carbon-components-react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { endpoints } from '@ibm-watson/shared/modules/ia/util/config';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { sharedMessages } from '../../messages';
import { LiveAgentTab as tabMessages } from '../../web-chat/messages';
import { links } from '../links';
import { Salesforce as messages } from '../messages';

const IDS = {
  FIELD_LABEL: 'salesforce-field-label',
  VISUALFORCE_PAGE: 'salesforce-visualforce-page'
};

export const VisualForce = () => {
  const { formatMessage: t } = useIntl();

  const generateVisualforceCode = () => {
    return `<apex:page standardController="LiveChatTranscript" >
  <iframe src="${endpoints.webChat}/loadAgentAppFrame.html?session_history_key={! LiveChatTranscript.x_watson_assistant_key__c }" style="box-sizing: border-box; width: 100%; height: calc(100vh - 20px); margin:0; padding: 0; border: 0;" />
</apex:page>`;
  };

  return (
    <div>
      <div className="TabContent__field-container">
        <h4>
          <FormattedMessage {...messages.createPageTitle} />
        </h4>
        <div className="TabContent__spacer8"></div>
        <div className="TabContent__subtitle">
          <FormattedMessage {...messages.createPageDescription} />
        </div>
        <h5 className="TabContent__inner-title TabContent__inner-title-heading">
          <FormattedMessage {...messages.createCustomFieldTitle} />
        </h5>
        <p>
          <FormattedMessage
            {...messages.createCustomFieldDescription}
            values={{
              fieldsAndRelationshipsSettingRoute: (
                <strong>
                  {messages.fieldsAndRelationshipsSettingRoute.defaultMessage}
                </strong>
              )
            }}
          />
        </p>
        <div className="TabContent__spacer16"></div>
        <UnorderedList className="TabContent__unordered-list">
          <ListItem>
            <FormattedMessage
              {...messages.createCustomFieldItem1}
              values={{
                createTokenCustomField: (
                  <strong>
                    {messages.createTokenCustomField.defaultMessage}
                  </strong>
                )
              }}
            />
          </ListItem>
          <ListItem>
            <FormattedMessage
              {...messages.createCustomFieldItem2}
              values={{
                createDataTypeCustomField: (
                  <strong>
                    {messages.createDataTypeCustomField.defaultMessage}
                  </strong>
                )
              }}
            />
          </ListItem>
          <ListItem>
            <FormattedMessage
              {...messages.createCustomFieldItem3}
              values={{
                createFieldLabelCustomField: (
                  <strong>
                    {messages.createFieldLabelCustomField.defaultMessage}
                  </strong>
                )
              }}
            />
          </ListItem>
          <ListItem>
            <FormattedMessage
              {...messages.createCustomFieldItem4}
              values={{
                createFieldLengthCustomField: (
                  <strong>
                    {messages.createFieldLabelCustomField.defaultMessage}
                  </strong>
                )
              }}
            />
          </ListItem>
        </UnorderedList>
        <div className="TabContent__spacer16"></div>
        <h5 className="TabContent__inner-title TabContent__inner-title-heading">
          <FormattedMessage {...messages.createVisualForcePageTitle} />
        </h5>
        <p>
          <FormattedMessage
            {...messages.createVisualForcePageDescription}
            values={{
              visualforcePagesLabel: (
                <strong>{messages.visualforcePagesLabel.defaultMessage}</strong>
              ),
              availableForLightningLabel: (
                <em>{messages.availableForLightningLabel.defaultMessage}</em>
              )
            }}
          />
        </p>
        <div className="TabContent__spacer16"></div>
        <div className="TabContent__description">
          <CodeSnippet
            id={IDS.VISUALFORCE_PAGE}
            feedback={t(sharedMessages.copied)}
            copyButtonDescription={t(sharedMessages.copyToClipboard)}
            copyLabel={t(sharedMessages.copyToClipboard)}
            type="multi"
            onClick={() => copyContentToClipboard(generateVisualforceCode())}>
            {generateVisualforceCode()}
          </CodeSnippet>
        </div>
        <div className="TabContent__spacer16"></div>
        <h5 className="TabContent__inner-title TabContent__inner-title-heading">
          <FormattedMessage {...messages.agentConsoleTitle} />
        </h5>
        <p>
          <FormattedMessageWithLink
            url={
              useIsNewIA()
                ? links.learnMoreSalesforceAddPageNewIA
                : links.learnMoreSalesforceAddPageClassic
            }
            {...messages.agentConsoleDescription}
          />
        </p>
      </div>
      <div className="ServiceDesk__divider"></div>
      <div className="TabContent__spacer16"></div>
      <div className="TabContent__field-container">
        <h4>
          <FormattedMessage {...tabMessages.transferSupportTitle} />
        </h4>
        <div className="TabContent__spacer8"></div>
        <div className="TabContent__subtitle">
          <FormattedMessageWithLink
            url={
              useIsNewIA()
                ? links.learnMoreTransferSupportNewIA
                : links.learnMoreTransferSupportClassic
            }
            {...tabMessages.transferSupportDescription}
          />
        </div>
      </div>
    </div>
  );
};
